import React from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { getRepresentatives } from '../../../services/projectRepresentatives';
import { readOnlyMessage } from '../../../services/readOnlyNotice';
import {
    DEFAULT_NOTFALLSCHRITTE, DEFAULT_REGELN, istGesperrt, leereOrdnung, sperrHinweis, verbinde,
} from '../handbuchLogic';
import type { ReportMetadata } from '../../../types';
import type { NotrufplanInhalt } from '../../sige-plan/types';
import type { Baustellenordnung as BaustellenordnungDaten, Handbuch } from '../types';
import { A3Seite } from './A3Seite';
import { Abschnitt, AbschnittLeer } from './Abschnitt';
import { HANDBUCH_FARBEN } from './stil';

/**
 * Kapitel 4 — Baustellenordnung.
 *
 * Portiert aus `pages/editor/BaustellenordnungPage.tsx` der Quell-App (149 Z.).
 * Die Vorbelegungen (DEFAULT_REGELN :7-13, NOTFALL_SCHRITTE :15-21) liegen als
 * Konstanten in `handbuchLogic.ts` und werden von dort geholt — sie speisen
 * ausserdem den Einweisungsvorschlag, und zwei Kopien desselben Textes gehen
 * frueher oder spaeter auseinander.
 *
 * ═══ DAS EINZIGE KAPITEL, DAS EIGENE DATEN ERFASST ═════════════════════════
 * Alles, was hier eingegeben wird, landet in `handbuecher.ordnung` — einer
 * eigenen Spalte des Handbuchsatzes. In Bestandsdaten wird NICHT geschrieben:
 * die Notrufdaten kommen aus dem Notrufplan des SiGe-Plans und werden hier nur
 * angezeigt. Gaebe es hier ein zweites Feld „Krankenhaus", stuenden zwei
 * Krankenhaeuser im selben Projekt, und niemand wuesste, welches gilt.
 *
 * ═══ ZWEI REIHEN SCHREIBSCHUTZ ═════════════════════════════════════════════
 * `isReadOnly` schaltet jede Eingabe ab, und eine freigegebene Fassung ist
 * zusaetzlich eingefroren (`istGesperrt`). Beides wird in `aendere()` noch
 * einmal geprueft, bevor irgendetwas nach oben gemeldet wird. Die dritte Reihe
 * steht im Dienst (`pruefeAenderbar` und `.eq('status','entwurf')` im UPDATE).
 *
 * ═══ EINE ABWEICHUNG VON DER VORLAGE, MIT GRUND ════════════════════════════
 * Die Vorlage druckte bei leerer Regelliste stillschweigend ihre
 * Vorschlagsliste. Damit stuenden Regeln auf einem unterschriebenen Aushang,
 * die niemand beschlossen hat. Hier bleibt die Liste sichtbar leer und traegt
 * den Vorschlag als KNOPF — uebernommen wird er durch einen Menschen.
 */

type SprungZiel = 'stammdaten' | 'notrufplan';

interface Props {
    /** Handbuchkopf. `ordnung` ist die Quelle dieses Kapitels. */
    handbuch: Handbuch | null;
    /** Projektstammdaten: Bauleitung vor Ort und Koordinator für die
     *  Rufnummernliste. Wird nur gelesen. */
    metadata: ReportMetadata;
    /** Inhalt des Blocks `sige_bloecke` mit `typ='notrufplan'`. Wird nur
     *  gelesen — der Notrufplan gehört dem SiGe-Plan. */
    notrufplan?: NotrufplanInhalt;
    /** Kopfzeile der Seite, üblicherweise ein `<SeitenKopf …/>`. */
    kopf?: React.ReactNode;
    /** Fußzeile der Seite, üblicherweise ein `<SeitenFuss …/>`. */
    fuss?: React.ReactNode;
    /** Nur-Ansicht (aus „Offene Positionen" geöffnet oder fremde Projektsperre). */
    isReadOnly?: boolean;
    /**
     * Meldet die geänderte Ordnung nach oben. Fehlt der Rückruf, ist dieses
     * Kapitel eine reine Ansicht — genau das braucht der Druck.
     */
    onAendern?: (naechste: BaustellenordnungDaten) => void;
    onSprung?: (ziel: SprungZiel) => void;
}

// ═══════════════════════════════════════════════════════════════════════════
// REINE FUNKTIONEN
//
// Gehoeren fachlich nach `handbuchLogic.ts`; sie stehen hier, weil diese Datei
// sie zuerst braucht (siehe Rueckmeldung). Nicht exportiert, sonst meldet
// `react-refresh/only-export-components` bei `--max-warnings 0`.
// ═══════════════════════════════════════════════════════════════════════════

/** Setzt einen Listeneintrag, ohne die Liste an anderer Stelle anzufassen. */
const ersetzeAn = (liste: string[], index: number, wert: string): string[] =>
    liste.map((alt, i) => (i === index ? wert : alt));

/**
 * Die Zeilen der Rufnummernliste.
 *
 * 112, 110 und der Giftnotruf stehen FEST und werden nirgends erfasst — sie
 * sind auf jeder Baustelle dieselben, und ein Eingabefeld dafuer waere eine
 * Einladung, sie falsch zu ueberschreiben. Wortgleich zum Notrufpunkt der
 * Einweisung (`services/sigeEinweisung.ts`), damit der Eingewiesene nichts
 * anderes hoert, als am Bauzaun haengt.
 */
interface Rufnummer {
    bezeichnung: string;
    wert: string;
    /** Was dasteht, wenn nichts hinterlegt ist. */
    fehlt?: string;
    ziel?: SprungZiel;
    fett?: boolean;
}

const rufnummern = (
    metadata: ReportMetadata,
    notrufplan: NotrufplanInhalt | undefined,
): Rufnummer[] => {
    const sigeko = getRepresentatives(metadata)[0];
    return [
        { bezeichnung: 'Rettungsdienst / Feuerwehr', wert: '112', fett: true },
        { bezeichnung: 'Polizei', wert: '110', fett: true },
        { bezeichnung: 'Giftnotruf', wert: '030 19240' },
        {
            bezeichnung: 'Nächstes Krankenhaus',
            wert: verbinde([notrufplan?.krankenhaus?.name, notrufplan?.krankenhaus?.telefon]),
            fehlt: 'im Notrufplan des SiGe-Plans nicht hinterlegt',
            ziel: 'notrufplan',
        },
        {
            bezeichnung: 'Durchgangsarzt',
            wert: verbinde([notrufplan?.durchgangsarzt?.name, notrufplan?.durchgangsarzt?.telefon]),
            fehlt: 'im Notrufplan des SiGe-Plans nicht hinterlegt',
            ziel: 'notrufplan',
        },
        {
            bezeichnung: 'Bauleitung',
            wert: verbinde([
                metadata.siteContactPerson || metadata.siteContactCompany,
                metadata.siteContactPhone,
            ]),
            fehlt: 'in den Projektdaten nicht hinterlegt',
            ziel: 'stammdaten',
        },
        {
            bezeichnung: 'SiGeKo',
            wert: verbinde([
                sigeko?.name || metadata.coordinator,
                sigeko?.phone || metadata.coordinatorPhone,
            ]),
            fehlt: 'in den Projektdaten nicht hinterlegt',
            ziel: 'stammdaten',
        },
    ];
};

// ═══════════════════════════════════════════════════════════════════════════
// BAUSTEINE
// ═══════════════════════════════════════════════════════════════════════════

const feldStil: React.CSSProperties = {
    width: '100%',
    border: `1px solid ${HANDBUCH_FARBEN.linie}`,
    borderRadius: '3px',
    padding: '4px 6px',
    fontSize: '12px',
    fontFamily: 'inherit',
};

/** Eine Angabe im gedruckten Blatt: Wert oder ehrlicher Ersatztext. */
const Angabe: React.FC<{
    bezeichnung: string;
    wert?: string;
    fehlt: string;
    ziel?: SprungZiel;
    onSprung?: (ziel: SprungZiel) => void;
    fett?: boolean;
    breite?: string;
}> = ({ bezeichnung, wert, fehlt, ziel, onSprung, fett, breite = '120px' }) => (
    <tr style={{ borderBottom: '1px solid #eeeeee' }}>
        <td style={{
            padding: '3px 8px 3px 0',
            width: breite,
            verticalAlign: 'top',
            color: HANDBUCH_FARBEN.textLeise,
            fontWeight: 500,
        }}>
            {bezeichnung}
        </td>
        <td style={{ padding: '3px 0', verticalAlign: 'top', whiteSpace: 'pre-wrap' }}>
            {wert?.trim()
                ? <span style={{ fontWeight: fett ? 700 : 400 }}>{wert.trim()}</span>
                : (
                    <span style={{ fontStyle: 'italic', color: HANDBUCH_FARBEN.statusFehlt }}>
                        {fehlt}
                        {ziel && onSprung && (
                            <button
                                type="button"
                                onClick={() => onSprung(ziel)}
                                className="print:hidden ml-2 underline"
                                style={{ fontStyle: 'normal', fontSize: '8px', color: HANDBUCH_FARBEN.textLeise }}
                            >
                                → hinterlegen
                            </button>
                        )}
                    </span>
                )}
        </td>
    </tr>
);

/** Ein Listenfeld der Erfassung: bearbeiten, entfernen, ergaenzen. */
const ListenFeld: React.FC<{
    titel: string;
    werte: string[];
    vorschlag: string[];
    onAendern: (naechste: string[]) => void;
}> = ({ titel, werte, vorschlag, onAendern }) => (
    <div>
        <div className="flex items-center justify-between mb-1">
            <span className="text-sm font-semibold text-gray-700">{titel}</span>
            <div className="flex items-center gap-2">
                {werte.length === 0 && (
                    /* Der Vorschlag wird angeboten, nie stillschweigend
                       gedruckt: was auf dem Aushang steht, hat jemand
                       beschlossen. */
                    <button
                        type="button"
                        onClick={() => onAendern([...vorschlag])}
                        className="text-xs text-blue-600 underline"
                    >
                        Vorschlag übernehmen ({vorschlag.length})
                    </button>
                )}
                <button
                    type="button"
                    onClick={() => onAendern([...werte, ''])}
                    className="text-xs text-gray-600 flex items-center gap-1 hover:text-gray-900"
                >
                    <Plus size={12} /> Zeile
                </button>
            </div>
        </div>
        {werte.length === 0 ? (
            <p className="text-xs text-gray-500 italic">Noch keine Zeile erfasst.</p>
        ) : (
            <div className="flex flex-col gap-1">
                {werte.map((wert, i) => (
                    // Der Schluessel ist der Index und nicht der Text: waehrend
                    // des Tippens ist der Text nicht eindeutig (zwei leere
                    // Zeilen), und React zoege dann den Fokus aus dem Feld.
                    <div key={i} className="flex items-start gap-1">
                        <span className="text-xs text-gray-400 w-4 text-right pt-1">{i + 1}.</span>
                        <input
                            type="text"
                            value={wert}
                            onChange={e => onAendern(ersetzeAn(werte, i, e.target.value))}
                            style={feldStil}
                        />
                        <button
                            type="button"
                            onClick={() => onAendern(werte.filter((_, j) => j !== i))}
                            className="text-gray-400 hover:text-red-600 pt-1"
                            title="Zeile entfernen"
                        >
                            <Trash2 size={13} />
                        </button>
                    </div>
                ))}
            </div>
        )}
    </div>
);

export const Baustellenordnung: React.FC<Props> = ({
    handbuch, metadata, notrufplan, kopf, fuss,
    isReadOnly = false, onAendern, onSprung,
}) => {
    const gesperrt = istGesperrt(handbuch);
    const sperre = sperrHinweis(handbuch);
    // Ohne Handbuchsatz gibt es nichts zu aendern; angezeigt wird dann eine
    // leere Ordnung, damit das Blatt trotzdem druckbar bleibt.
    const ordnung = handbuch?.ordnung ?? leereOrdnung();
    const bearbeitbar = !!onAendern && !!handbuch && !isReadOnly && !gesperrt;

    /**
     * Zweite Reihe des Schreibschutzes: greift auch dann, wenn ein Feld wider
     * Erwarten bedienbar ist oder `isReadOnly` zur Laufzeit kippt, weil die
     * Projektsperre an jemand anderen gegangen ist.
     */
    const aendere = (teil: Partial<BaustellenordnungDaten>): void => {
        if (!bearbeitbar || !onAendern) return;
        onAendern({ ...ordnung, ...teil });
    };

    const ersthelfer = (notrufplan?.ersthelfer || [])
        .map(e => verbinde([e.name, e.firma], ', '))
        .filter(Boolean)
        .join(' | ');

    return (
        <div className="flex flex-col gap-4">

            {/* ═══ ERFASSUNG — steht ueber dem Blatt und faellt im Druck weg ═══ */}
            {onAendern && (
                <div className="print:hidden rounded-lg border border-gray-200 bg-white p-4">
                    <h3 className="text-sm font-semibold text-gray-800 mb-3">
                        Baustellenordnung erfassen
                    </h3>

                    {!handbuch && (
                        <p className="text-xs text-amber-700 mb-3">
                            Für dieses Projekt ist noch kein Handbuch angelegt. Die Baustellenordnung
                            gehört zum Handbuchsatz und kann erst danach erfasst werden.
                        </p>
                    )}
                    {sperre && <p className="text-xs text-amber-700 mb-3">{sperre}</p>}
                    {isReadOnly && (
                        <p className="text-xs text-amber-700 mb-3">
                            {readOnlyMessage('die Baustellenordnung zu ändern')}
                        </p>
                    )}

                    <fieldset disabled={!bearbeitbar} className="flex flex-col gap-3 disabled:opacity-60">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <label className="flex flex-col gap-1">
                                <span className="text-xs font-medium text-gray-600">Arbeitszeiten</span>
                                <input
                                    type="text"
                                    value={ordnung.arbeitszeiten}
                                    onChange={e => aendere({ arbeitszeiten: e.target.value })}
                                    placeholder="z. B. Mo–Fr 07:00–17:00 Uhr, Samstag nach Absprache"
                                    style={feldStil}
                                />
                            </label>
                            <label className="flex flex-col gap-1">
                                <span className="text-xs font-medium text-gray-600">Sammelplatz</span>
                                <input
                                    type="text"
                                    value={ordnung.sammelplatz}
                                    onChange={e => aendere({ sammelplatz: e.target.value })}
                                    placeholder="z. B. Parkplatz Nord, gegenüber der Einfahrt"
                                    style={feldStil}
                                />
                            </label>
                        </div>

                        <label className="flex flex-col gap-1">
                            <span className="text-xs font-medium text-gray-600">Zugangsregelung</span>
                            <textarea
                                value={ordnung.zugang}
                                onChange={e => aendere({ zugang: e.target.value })}
                                rows={2}
                                placeholder="Wer darf die Baustelle betreten, wie wird an- und abgemeldet, wo ist die Zufahrt?"
                                style={feldStil}
                            />
                        </label>

                        <ListenFeld
                            titel="Regeln"
                            werte={ordnung.regeln}
                            vorschlag={DEFAULT_REGELN}
                            onAendern={regeln => aendere({ regeln })}
                        />
                        <ListenFeld
                            titel="Verhalten im Notfall"
                            werte={ordnung.notfallschritte}
                            vorschlag={DEFAULT_NOTFALLSCHRITTE}
                            onAendern={notfallschritte => aendere({ notfallschritte })}
                        />
                    </fieldset>

                    <p className="text-[11px] text-gray-500 mt-3">
                        Die Regeln dieser Seite speisen den Aushang und den Vorschlag für die
                        Einweisung — sie sind an drei Stellen dasselbe und werden nur hier gepflegt.
                    </p>
                </div>
            )}

            {/* ═══ DAS BLATT ═══ */}
            <A3Seite
                kopf={kopf}
                fuss={fuss}
            >
                <div className="flex-1 flex gap-[6px] min-h-0">

                    {/* ═══ LINKS: die Ordnung ═══ */}
                    <div className="flex flex-col gap-[6px] min-h-0" style={{ width: '55%' }}>
                        <Abschnitt titel="Allgemeine Baustellenordnung" className="flex-1">
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '9px' }}>
                                <tbody>
                                    <Angabe
                                        bezeichnung="Arbeitszeiten"
                                        wert={ordnung.arbeitszeiten}
                                        fehlt="noch nicht festgelegt"
                                    />
                                    <Angabe
                                        bezeichnung="Zugangsregelung"
                                        wert={ordnung.zugang}
                                        fehlt="noch nicht festgelegt"
                                    />
                                    <Angabe
                                        bezeichnung="Sammelplatz"
                                        wert={ordnung.sammelplatz}
                                        fehlt="noch nicht festgelegt"
                                    />
                                </tbody>
                            </table>

                            <div style={{ marginTop: '8px' }}>
                                <div style={{
                                    fontSize: '9px',
                                    fontWeight: 600,
                                    color: HANDBUCH_FARBEN.textLeise,
                                    marginBottom: '3px',
                                }}>
                                    Regeln
                                </div>
                                {ordnung.regeln.filter(r => r.trim()).length === 0 ? (
                                    <AbschnittLeer text="Für dieses Projekt ist noch keine Regel beschlossen. Ein Aushang ohne Regeln ist kein Aushang — die Erfassung oben trägt einen Vorschlag." />
                                ) : (
                                    <ul style={{ fontSize: '9px', lineHeight: 1.5, listStyle: 'none', padding: 0, margin: 0 }}>
                                        {ordnung.regeln.filter(r => r.trim()).map((regel, i) => (
                                            <li key={`${i}-${regel}`} className="flex items-start gap-[5px]">
                                                <span style={{ color: HANDBUCH_FARBEN.textLeise, flexShrink: 0 }}>•</span>
                                                <span>{regel}</span>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        </Abschnitt>
                    </div>

                    {/* ═══ RECHTS: Notfall ═══ */}
                    <div className="flex flex-col gap-[6px] min-h-0" style={{ width: '45%' }}>

                        <Abschnitt titel="Notfallplanung / Erste Hilfe" style={{ flex: '0 0 auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '9px' }}>
                                <tbody>
                                    <Angabe
                                        bezeichnung="Ersthelfer"
                                        wert={ersthelfer}
                                        fehlt="im Notrufplan des SiGe-Plans nicht benannt"
                                        ziel="notrufplan"
                                        onSprung={onSprung}
                                    />
                                    <Angabe
                                        bezeichnung="Sammelplatz"
                                        wert={ordnung.sammelplatz}
                                        fehlt="noch nicht festgelegt"
                                    />
                                    <Angabe
                                        bezeichnung="Nächstes Krankenhaus"
                                        wert={verbinde([
                                            notrufplan?.krankenhaus?.name,
                                            notrufplan?.krankenhaus?.adresse,
                                        ], ', ')}
                                        fehlt="im Notrufplan des SiGe-Plans nicht hinterlegt"
                                        ziel="notrufplan"
                                        onSprung={onSprung}
                                    />
                                </tbody>
                            </table>
                        </Abschnitt>

                        <Abschnitt titel="Verhalten im Notfall" className="flex-1">
                            {ordnung.notfallschritte.filter(s => s.trim()).length === 0 ? (
                                <AbschnittLeer text="Noch kein Ablauf festgelegt. Die Erfassung oben trägt einen Vorschlag." />
                            ) : (
                                <ol style={{ fontSize: '9px', lineHeight: 1.5, listStyle: 'none', padding: 0, margin: 0 }}>
                                    {ordnung.notfallschritte.filter(s => s.trim()).map((schritt, i) => (
                                        <li key={`${i}-${schritt}`} className="flex items-start gap-[6px]">
                                            <span style={{
                                                fontWeight: 700,
                                                color: HANDBUCH_FARBEN.textLeise,
                                                width: '14px',
                                                textAlign: 'right',
                                                flexShrink: 0,
                                            }}>
                                                {i + 1}.
                                            </span>
                                            <span>{schritt}</span>
                                        </li>
                                    ))}
                                </ol>
                            )}
                        </Abschnitt>

                        <Abschnitt titel="Wichtige Rufnummern" style={{ flex: '0 0 auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '9px' }}>
                                <tbody>
                                    {rufnummern(metadata, notrufplan).map(r => (
                                        <Angabe
                                            key={r.bezeichnung}
                                            bezeichnung={r.bezeichnung}
                                            wert={r.wert}
                                            fehlt={r.fehlt || '—'}
                                            ziel={r.ziel}
                                            onSprung={onSprung}
                                            fett={r.fett}
                                            breite="140px"
                                        />
                                    ))}
                                </tbody>
                            </table>
                        </Abschnitt>
                    </div>
                </div>
            </A3Seite>
        </div>
    );
};
